import { createSelector } from 'reselect';
import moment from 'moment-timezone';
import {
  __,
  allPass,
  always,
  isEmpty,
  any,
  equals,
  gte,
  ifElse,
  isNil,
  lt,
  multiply,
  not,
  pathEq,
  path,
  pipe,
  prop,
  propSatisfies,
  subtract,
  sum,
  T,
  compose,
  mapObjIndexed,
  values,
  lte,
  keys,
  pickBy,
  invertObj,
  find,
  filter,
  complement,
  map,
  sortBy,
  unfold,
  clone,
} from 'ramda';
import { selectCurrentExperience, selectExperienceGroupLimits, selectCurrentExperienceTimezone } from '../experience';
import { BookingStep } from '../../routes/booking/booking.constants';
import { TICKET_TYPES_ORDERED, WEEKDAYS } from './booking.constants';
import { setNoon } from '../../shared/utils/time';

const orderByTime = sortBy(({ start }) => parseInt(start.replace(':', ''), 10));

export const selectBooking = prop('booking');

export const selectIsBookingActive = createSelector(
  selectBooking,
  prop('isBookingActive')
);

export const selectBookingStep = createSelector(
  selectBooking,
  prop('step')
);

export const selectBookingSlots = createSelector(
  selectBooking,
  compose(
    orderByTime,
    prop('timeSlots')
  )
);

export const selectBookingDate = createSelector(
  selectBooking,
  prop('date')
);

export const selectBookingNotes = createSelector(
  selectBooking,
  prop('notes')
);

export const selectBookingPaymentStatus = createSelector(
  selectBooking,
  prop('paymentStatus')
);

export const selectBookingWeekday = createSelector(
  selectBookingDate,
  selectCurrentExperienceTimezone,
  (date, timezone) =>
    date &&
    moment
      .unix(date)
      .tz(timezone)
      .format('dddd')
      .toLowerCase()
);

const inactivatePastOnes = (date, timezone) => {
  const now = moment();
  const slotMarketTime = time => {
    const slotTimeString = `${moment.unix(date).format('YYYY MM DD')} ${time}`;
    return moment.tz(slotTimeString, 'YYYY MM DD HH:mm', timezone);
  };

  return map(slot => ({
    ...slot,
    isActive: slotMarketTime(slot.start) >= now,
  }));
};

export const selectBookingTimeslots = createSelector(
  selectCurrentExperience,
  selectBookingWeekday,
  selectBookingDate,
  selectCurrentExperienceTimezone,
  ({ timeSlots }, weekday, date, timezone) =>
    pipe(
      orderByTime,
      inactivatePastOnes(date, timezone)
    )(timeSlots[weekday] || [])
);

export const selectBookingTickets = createSelector(
  selectBooking,
  prop('tickets')
);

export const selectSelectedTicketsCount = createSelector(
  selectBookingTickets,
  pipe(
    values,
    sum
  )
);

export const selectIsStepFilled = createSelector(
  selectBooking,
  selectCurrentExperience,
  selectSelectedTicketsCount,
  ({ step, ...booking }, { minGroupSize = 0, maxGroupSize }, ticketsCount) => {
    const validators = {
      [BookingStep.TIME]: ({ date, timeSlots }) => !isNil(date) && !isEmpty(timeSlots),
      [BookingStep.PARTICIPANTS]: ({ tickets }) =>
        any(propSatisfies(lt(0), __, tickets), TICKET_TYPES_ORDERED) &&
        allPass([lte(minGroupSize), gte(maxGroupSize || Number.MAX_VALUE)])(ticketsCount),
      [BookingStep.PAYMENT]: T,
      [BookingStep.SUMMARY]: T,
    };

    return validators[step](booking);
  }
);

export const selectIsGroupExperience = createSelector(
  selectCurrentExperience,
  pathEq(['tickets', 'isGroup'], true)
);

export const selectGroupPrice = createSelector(
  selectCurrentExperience,
  compose(
    val => parseFloat(val),
    path(['tickets', 'groupPrice'])
  )
);

export const selectSubtotalPrice = createSelector(
  selectCurrentExperience,
  selectBookingTickets,
  selectIsGroupExperience,
  selectGroupPrice,
  (experience, selectedTickets, isGroupExperience, groupPrice) =>
    ifElse(equals(true), always(groupPrice), () =>
      sum(
        compose(
          values,
          mapObjIndexed((value, key) => parseFloat(path(['tickets', 'types', key, 'price'], experience)) * value)
        )(selectedTickets)
      )
    )(isGroupExperience)
);

export const selectHasDiscountPossibility = createSelector(
  selectCurrentExperience,
  selectIsGroupExperience,
  (experience, isGroupExperience) =>
    allPass([
      always(not(isGroupExperience)),
      always(!!experience.discountPercent),
      always(!!experience.discountThreshold),
    ])()
);

export const selectDiscount = createSelector(
  selectCurrentExperience,
  selectHasDiscountPossibility,
  selectSelectedTicketsCount,
  selectSubtotalPrice,
  (experience, hasDiscountPossibility, selectSelectedTicketsCount, subtotalPrice) =>
    ifElse(
      allPass([always(hasDiscountPossibility), always(gte(selectSelectedTicketsCount, experience.discountThreshold))]),
      pipe(
        always(experience.discountPercent / 100),
        multiply(subtotalPrice),
        price => +price.toFixed(2)
      ),
      always(0)
    )()
);

export const selectBookingPrice = createSelector(
  selectSubtotalPrice,
  selectDiscount,
  compose(
    price => +price.toFixed(2),
    subtract
  )
);

export const selectAreTicketsFull = createSelector(
  selectExperienceGroupLimits,
  selectSelectedTicketsCount,
  ({ max: maxTickets }, ticketsCount) => maxTickets > 0 && ticketsCount >= maxTickets
);

export const selectBookingCode = createSelector(
  selectBooking,
  prop('code')
);

export const selectDisabledWeekdays = createSelector(
  selectCurrentExperience,
  compose(
    map(parseInt),
    values,
    activeDays => pickBy((val, key) => isNil(find(equals(key), activeDays)), invertObj(WEEKDAYS)),
    keys,
    filter(complement(isEmpty)),
    prop('timeSlots')
  )
);

export const selectFirstAvailableDate = createSelector(
  selectCurrentExperience,
  selectCurrentExperienceTimezone,
  selectDisabledWeekdays,
  (experience, timezone, disabledWeekdays) => {
    const minDate = moment()
      .add(experience.leadTime, 'd')
      .tz(timezone);

    if (equals(disabledWeekdays.length, 7)) {
      return null;
    }

    const weekdays = unfold(days => (days > 6 ? false : [clone(minDate).add(days, 'day'), days + 1]))(0);

    const founded = find(date => {
      const isDisabled = disabledWeekdays.includes(date.day());
      if (isDisabled) {
        return false;
      }

      const dayName = date.format('dddd').toLowerCase();
      const timeSlots = experience.timeSlots[dayName];

      return find(({ start }) => {
        const [hours, minutes] = start.split(':');
        const currentTimeSlotDate = date
          .clone()
          .hours(hours)
          .minutes(minutes);

        return currentTimeSlotDate.isAfter(minDate);
      })(timeSlots);
    })(weekdays);

    return setNoon(founded);
  }
);
